import { z } from "zod";

/**
 * The single reader of import.meta.env. Validates at module load so a
 * misconfigured build fails at boot, loudly, instead of at first request
 * with an undefined baseURL. ESLint bans import.meta.env everywhere else.
 *
 * Optionality mirrors the committed .env.* files and the CI pipeline:
 * - VITE_WORKOS_CLIENT_ID is absent from .env.demo (the pipeline injects it
 *   as a build-time env var), so it is optional here.
 */
const EnvSchema = z.object({
  VITE_WORKOS_CLIENT_ID: z.string().min(1).optional(),
  // Tolerates empty-string injection like the old `|| "api.workos.com"` did.
  VITE_WORKOS_API_HOSTNAME: z
    .string()
    .optional()
    .transform((v) => (v && v.trim() ? v.trim() : "api.workos.com")),
  VITE_WORKOS_DEV_MODE: z
    .string()
    .optional()
    .transform((v) => v === "true"),

  VITE_AGENT_API_BASE_URL: z.url(),
  VITE_PROFILE_API_DOMAIN: z.url(),
  VITE_BODYCAM_API_DOMAIN: z.url(),
  VITE_JOBS_API_BASE_URL: z.url(),
  VITE_NEWS_API_BASE_URL: z.url(),
  VITE_UPLOAD_API_BASE_URL: z.url(),
  VITE_RMS_API_BASE_URL: z.url(),

  /**
   * Origin of the Investigator client, which now owns incident management in
   * its own repo and on its own subdomain. Optional: when it is absent the
   * sidebar simply omits the entry, so a build that predates the split (or a
   * local build with no value set) degrades to hiding the link rather than
   * offering a dead one.
   */
  VITE_INVESTIGATOR_APP_URL: z.url().optional(),


  VITE_WEBSOCKETS_DOMAIN: z.string().startsWith("wss://"),
  // Invalid values fall back to 40 (the old Number(...)-with-fallback behavior).
  VITE_MAX_VIDEOS_PER_REPORT: z.coerce.number().int().positive().default(40).catch(40),

  // Amazon Location Service (Places and Maps) API key + region for address
  // autocomplete, geocoding, and map tiles. It is a scoped, referer-restricted
  // public key
  // (`v1.public.…`), provisioned per environment by the infra CDK stack.
  // Optional so the app still boots in environments where it has not yet been
  // provisioned — callers degrade to plain manual address entry when absent.
  VITE_LOCATION_API_KEY: z
    .string()
    .optional()
    .transform((v) => {
      const trimmed = v?.trim();
      return trimmed ? trimmed : undefined;
    }),
  // Default MUST match LOCATION_SERVICE_REGION in packages/infra/lib/types/consts.ts
  // (the region the API key is provisioned in) — otherwise requests hit a region
  // the key is not scoped to and fail.
  VITE_LOCATION_REGION: z
    .string()
    .optional()
    .transform((v) => (v && v.trim() ? v.trim() : "us-west-2")),
});

const parsed = EnvSchema.safeParse(import.meta.env);
if (!parsed.success) {
  console.error("[env] invalid VITE_ configuration:", z.treeifyError(parsed.error));
  // This throw happens at module scope, before React mounts — no error
  // boundary can catch it. Paint a readable failure into #root instead of
  // shipping a blank page on deploy misconfiguration.
  const root = document.getElementById("root");
  if (root) {
    const box = document.createElement("div");
    box.style.cssText = "padding:32px;font-family:system-ui,sans-serif;text-align:center";
    const heading = document.createElement("h2");
    heading.textContent = "Configuration error";
    const message = document.createElement("p");
    message.textContent =
      "This deployment is missing or has invalid environment configuration. " +
      "See the browser console for details.";
    box.append(heading, message);
    root.replaceChildren(box);
  }
  throw new Error("Invalid environment configuration; see console for details.");
}

export const env = Object.freeze({
  workos: {
    /** May be undefined in local demo-mode builds; the pipeline injects it in CI. */
    clientId: parsed.data.VITE_WORKOS_CLIENT_ID,
    apiHostname: parsed.data.VITE_WORKOS_API_HOSTNAME,
    devMode: parsed.data.VITE_WORKOS_DEV_MODE,
  },
  api: {
    agent: parsed.data.VITE_AGENT_API_BASE_URL,
    profile: parsed.data.VITE_PROFILE_API_DOMAIN,
    bodycam: parsed.data.VITE_BODYCAM_API_DOMAIN,
    jobs: parsed.data.VITE_JOBS_API_BASE_URL,
    news: parsed.data.VITE_NEWS_API_BASE_URL,
    upload: parsed.data.VITE_UPLOAD_API_BASE_URL,
    rms: parsed.data.VITE_RMS_API_BASE_URL,
  },
  apps: {
    /** Undefined in builds that predate the Investigator split. */
    investigator: parsed.data.VITE_INVESTIGATOR_APP_URL,
  },
  websocketsDomain: parsed.data.VITE_WEBSOCKETS_DOMAIN,
  maxVideosPerReport: parsed.data.VITE_MAX_VIDEOS_PER_REPORT,
  location: {
    /** Undefined until the Amazon Location API key is provisioned for this env. */
    apiKey: parsed.data.VITE_LOCATION_API_KEY,
    region: parsed.data.VITE_LOCATION_REGION,
  },
} as const);

